import {useState} from "react";
import {signOutEverywhere} from "./firebase";
import {TodoDrawer} from "./todo/TodoDrawer";
import {useSession} from "./auth/useSession";
import {NotRegistered} from "./screens/NotRegistered";
import {SignIn} from "./screens/SignIn";
import {Today} from "./screens/Today";
import {DEMO_BOARD, DEMO_ME} from "./dev/fixture";
import {BlindProvider, BlindToggle} from "./ui/Blind";
import {Icon} from "./ui/Icon";
import {ToastProvider} from "./ui/Toasts";

declare const __APP_VERSION__: string;

const PROFILE_TR: Record<string, string> = {
  admin: "yönetici", frontdesk: "ön büro", provider: "danışman", intern: "stajyer",
};

// The header (Onur, 15.10.2026): the logo alone on the left; on the
// right the name (its e-mail and role in the tooltip), Yapılacaklar,
// the eye and Çıkış as a round power button.
function TopBar({name, email, role, money, todoOpen, onTodo, onSignOut}: {
  name: string; email: string; role: string; money: boolean;
  todoOpen: boolean; onTodo: () => void; onSignOut: () => void;
}) {
  return (
    <header className="top">
      <img className="brand" src="/nefes-logo.svg" alt="Nefes" />
      <span className="spacer" />
      <span className="who" title={`${email} · ${role}`}>{name}</span>
      <button className="todo-btn" aria-haspopup="dialog" aria-expanded={todoOpen}
        onClick={onTodo}>☑ Yapılacaklar</button>
      {money && <BlindToggle />}
      <button className="round" onClick={onSignOut} aria-label="Çıkış" title="Çıkış">
        <Icon name="power" />
      </button>
    </header>
  );
}

export default function App() {
  const session = useSession();
  const [todo, setTodo] = useState(false);

  // Dev only: the grid on a fixture day, no sign-in — for working on
  // its shape and words. Never in a production build.
  if (import.meta.env.DEV && window.location.search.includes("demo")) {
    return <DemoGrid />;
  }

  let body;
  switch (session.state) {
  case "loading":
    body = <main className="card-page"><p className="muted">Yükleniyor…</p></main>;
    break;
  case "signed_out":
    body = <SignIn />;
    break;
  case "not_registered":
    body = <NotRegistered email={session.email} />;
    break;
  case "error":
    body = (
      <main className="card-page">
        <section className="card">
          <h1>Bağlanılamadı</h1>
          <p className="error">{session.message}</p>
          <button className="primary" onClick={session.retry}>Tekrar dene</button>{" "}
          <button onClick={() => void signOutEverywhere()}>Çıkış</button>
        </section>
      </main>
    );
    break;
  case "ready":
    body = (
      <>
        <TopBar name={session.user.displayName ?? session.me.email} email={session.me.email}
          role={PROFILE_TR[session.me.profile] ?? session.me.profile} money={session.me.money}
          todoOpen={todo} onTodo={() => setTodo(true)} onSignOut={() => void signOutEverywhere()} />
        <Today me={session.me} />
        {todo && <TodoDrawer onClose={() => setTodo(false)} />}
      </>
    );
    break;
  }

  return (
    <ToastProvider>
      <BlindProvider>
        <div className="app">
          {body}
          <footer className="foot muted small">Nefes gün görünümü · {__APP_VERSION__}</footer>
        </div>
      </BlindProvider>
    </ToastProvider>
  );
}

function DemoGrid() {
  const money = !window.location.search.includes("desk");
  return (
    <ToastProvider>
      <BlindProvider>
        <div className="app">
          <TopBar name="DEMO" email={DEMO_ME.email} role={money ? "yönetici" : "ön büro"}
            money={money} todoOpen={false} onTodo={() => undefined} onSignOut={() => undefined} />
          <Today me={{...DEMO_ME, money}} demo={DEMO_BOARD} />
        </div>
      </BlindProvider>
    </ToastProvider>
  );
}
